import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {Observable, of, shareReplay} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {FaehigkeitenService} from './faehigkeiten.service';
import {IFaehigkeit} from '../shared/model/faehigkeit.model';
import {PagedResponse, Requests, RequestType} from '../shared/model/request.model';

export type RequestProblem = 'oneLanguageExpected' | 'bothLanguagesRequired' | 'alreadyExists';

const REQUEST_PROBLEM_TYPES: Record<string, RequestProblem> = {
  '/entity-name-one-language-expected': 'oneLanguageExpected',
  '/entity-name-both-languages-required': 'bothLanguagesRequired',
  '/entity-name-already-exists': 'alreadyExists',
};

/** Unterscheidung nur über den Typ-URI: ein errorKey kommt im Body nie an. */
export function requestProblemOf(err: unknown): RequestProblem | null {
  const type = (err as HttpErrorResponse)?.error?.type;
  if (typeof type !== 'string') return null;
  const suffix = Object.keys(REQUEST_PROBLEM_TYPES).find(s => type.endsWith(s));
  return suffix ? REQUEST_PROBLEM_TYPES[suffix] : null;
}

@Injectable({providedIn: 'root'})
export class RequestsService {
  public resourceUrlSkills = environment.apiConfig.uri + 'api/faehigkeiten';
  public resourceUrlTrainings = environment.apiConfig.uri + 'api/training';
  public resourceUrlIndustries = environment.apiConfig.uri + 'api/branchen';
  public resourceUrlMainFocus = environment.apiConfig.uri + 'api/schwerpunkte';
  public resourceUrlCertificates =
    environment.apiConfig.uri + 'api/zertifikatsname';

  constructor(protected http: HttpClient, private skillService: FaehigkeitenService) {
  }

  getRequests(
    page: number,
    size: number,
    type: RequestType,
    approved?: boolean
  ): Observable<PagedResponse<Requests>> {
    let params = new HttpParams().set('page', page).set('size', size);
    let url = this.getCurrentUrl(type);
    if (approved !== undefined) {
      params = params.set('approved', approved.toString());
    }
    return this.http.get<PagedResponse<Requests>>(url, {params});
  }

  // NP20-828: Vollstaendige Katalog-Liste (approved=true) mit shareReplay(1),
  // sodass mehrere Consumer (ConsultantComponent + PersonalDataComponent-Dropdowns)
  // denselben HTTP-Roundtrip teilen.
  //
  // Nutzt totalElements aus dem ersten Response, damit auch Kataloge mit mehr als
  // 300 Eintraegen komplett erfasst werden — sonst wuerden fehlende Eintraege als
  // Legacy gezaehlt. Bei HTTP-Fehler bleibt die Liste leer, statt still auf
  // "alles bekannt" zu degradieren.
  private readonly approvedStreams = new Map<RequestType, Observable<Requests[]>>();

  getApproved$(type: RequestType): Observable<Requests[]> {
    let stream = this.approvedStreams.get(type);
    if (!stream) {
      const initialPageSize = 300;
      stream = this.getRequests(0, initialPageSize, type, true).pipe(
        switchMap(first => {
          const firstItems = first.content ?? [];
          const total = first.page?.totalElements ?? firstItems.length;
          if (total <= firstItems.length) {
            return of(firstItems);
          }
          return this.getRequests(0, total, type, true).pipe(
            map(full => full.content ?? []),
            catchError(() => of(firstItems)),
          );
        }),
        catchError(() => of([] as Requests[])),
        shareReplay(1),
      );
      this.approvedStreams.set(type, stream);
    }
    return stream;
  }

  /** Convenience-Ableitung: nur die Namen als Set (fuer Legacy-Erkennung). */
  getApprovedNames$(type: RequestType): Observable<Set<string>> {
    return this.getApproved$(type).pipe(map(items => new Set(items.map(r => r.name))));
  }

  newRequest(type: RequestType, request: Requests) {
    let url = this.getCurrentUrl(type);
    return this.http.post(`${url}`, request);
  }

  /** Vorhandene Einträge (auch offene Anfragen), die in einer der beiden Sprachen gleich heißen. */
  findDuplicates(type: RequestType, draft: Requests): Observable<Requests[]> {
    const params = type == RequestType.SKILL
      ? new HttpParams().set('kategorie', draft.kategorie?.trim() ?? '').set('faehigkeit', draft.faehigkeit?.trim() ?? '')
      : new HttpParams().set('name', draft.name?.trim() ?? '');
    return this.http.get<Requests[]>(`${this.getCurrentUrl(type)}/duplicates`, {params});
  }

  updateRequest(type: RequestType, request: Requests) {
    if (type == RequestType.SKILL) {
      return this.editSkill(request);
    }
    let url = this.getCurrentUrl(type);
    return this.http.put(`${url}/${request.id}`, request);
  }

  deleteRequest(type: RequestType, request: Requests) {
    if (type == RequestType.SKILL) {
      return this.deleteSkill(request);
    }
    let url = this.getCurrentUrl(type);
    return this.http.delete(`${url}/${request.id}`);
  }

  acceptRequest(type: RequestType, element: Requests) {
    let url = this.getCurrentUrl(type);
    return this.http.post(`${url}/${element.id}/approve`, element.id);
  }

  deleteSkill(element: Requests) {
    return this.skillService.delete(element.id)
  }

  editSkill(element: Requests) {
    return this.skillService.update(element as IFaehigkeit)
  }

  private getCurrentUrl(type: RequestType): string {
    switch (type) {
      case RequestType.CERTIFICATE:
        return this.resourceUrlCertificates;
      case RequestType.MAINFOCUS:
        return this.resourceUrlMainFocus;
      case RequestType.INDUSTRY:
        return this.resourceUrlIndustries;
      case RequestType.SKILL:
        return this.resourceUrlSkills;
      case RequestType.TRAINING:
        return this.resourceUrlTrainings;
    }
  }
}
